import classNames from 'classnames';
import { ReactNode, useId } from 'react';

import { getComponentFromName } from '../../lib/utils';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import * as FieldComponents from '../fields/index';
import FieldLabel from './FieldLabel';

import styles from '../../../styles/fields/field-row.module.css';

interface FieldRowProps {
    name: string;
    type: string;
    label?: ReactNode | null;
    helpText?: ReactNode | null;
    errors?: string[] | null;
    fields?: Field[];
    className?: string;
    [key: string]: any;
}

function FieldRow({
    name,
    type,
    label = null,
    helpText = null,
    errors = null,
    fields = null,
    className = null,
    ...props
}: FieldRowProps) {
    const id = useId();
    const { fieldRow: fieldRowTheme = 'box' } = useTheme();
    const FieldComponent = getComponentFromName(FieldComponents, type) || null;
    const themeClassName = useThemeClassName(styles);
    return (
        <div
            key={`field-${name}`}
            className={classNames([
                styles.container,
                'field-row',
                `field-row-${type}`,
                {
                    [styles.isGroup]: type === 'group',
                    [styles.hasError]: errors !== null,
                },
                themeClassName,
                className,
            ])}
            data-theme={fieldRowTheme}
        >
            <div className={classNames([styles.inner, 'field-inner'])}>
                {label !== null ? (
                    <FieldLabel className={classNames([styles.label, 'field-label'])} htmlFor={id}>
                        {label}
                    </FieldLabel>
                ) : null}
                {FieldComponent !== null ? (
                    <FieldComponent
                        className={classNames([styles.field, 'field-input'])}
                        inputClassName={styles.input}
                        id={id}
                        type={type}
                        {...props}
                    />
                ) : null}
                {fields !== null
                    ? fields.map((subField) => (
                          <FieldRow key={subField.name} {...subField} className={styles.subField} />
                      ))
                    : null}
            </div>
            {helpText !== null ? (
                <FieldLabel className={classNames([styles.help, 'field-help'])}>
                    {helpText}
                </FieldLabel>
            ) : null}
            {errors !== null ? (
                <div className={classNames([styles.errors, 'field-errors'])}>
                    {errors.map((error, index) => (
                        <div key={`error-${index}`}>{error}</div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export default FieldRow;
